/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * The prev/next chevron artwork, per brand.
 *
 * IN ITS OWN MODULE ON PURPOSE. `src/__tests__/declared-never-read.test.ts`
 * only counts a `*_BY_BRAND` field as read when something OUTSIDE the declaring
 * file reads it — a map that is both written and consumed in one file is
 * indistinguishable from a knob nothing turns. `index.tsx` is that outside
 * reader, and the guard is what enforces it stays one.
 *
 * The evidence for every value here — the nine BG captures the 16x16 box is
 * byte-identical in, the decoded base64 glyph, and the owner's pixels for both
 * brands' ink — is in the header comment of `index.tsx`.
 */
import type { Brand } from '@/lib/brand/brand';

/**
 * One brand's prev/next chevron: the control box live paints it in, the glyph
 * inside that box, and the ink colour. All three are one artwork fact — see the
 * note at the top of this file for why they are not three tokens.
 */
export interface TickerArrowArt {
    /** The button's own box, CSS px, square. */
    boxSize: number;
    /** The glyph's coordinate space. Its aspect sizes the art inside the box. */
    viewBox: string;
    /** Path data for the leading / trailing chevron, in `viewBox` units. */
    left: string;
    right: string;
    /** How the path is painted. Live NM strokes; live BG fills. */
    paint: 'stroke' | 'fill';
    /** Stroke width in `viewBox` units. Ignored when `paint` is 'fill'. */
    strokeWidth?: number;
    /**
     * Ink colour. `null` = inherit the band's own --ticker-foreground, which is
     * what the brand measures TO — recorded as null rather than as a literal so
     * the band and its glyph cannot drift apart.
     */
    ink: string | null;
}

/**
 * THE TWO BRANDS GENUINELY DIFFER HERE, IN BOTH COLOUR AND ASPECT. This is not
 * a keyed switch whose arms agree — the arms are a stroked 1:1 white chevron
 * and a filled 0.6:1 grey one, from two different evidence lines.
 */
export const TICKER_ARROW_BY_BRAND: Record<Brand, TickerArrowArt> = {
    /**
     * nm — MEASURED, and confirmed twice (see the file header). The control box
     * is `w-5 h-5` = 20x20 in `.reference/captures/search-m-390/App.tsx:21-23`.
     * The glyph is this component's own stroked chevron, whose predicted ink
     * (4.25..11.58 of the 20-box) lands on the white pixels the owner captured
     * in `.reference/live-shots/nm-search-390.png`. Ink is pure
     * rgb(255,255,255) on black in those pixels, which is exactly
     * --ticker-foreground, so `ink: null` rather than a second copy of #fff.
     * Unchanged by the 2026-08-19 BG pass — same box, same path, same colour.
     */
    nm: {
        boxSize: 20,
        viewBox: '0 0 20 20',
        left: 'M10.833 15.833L5 10l5.833-5.833',
        right: 'M9.167 4.167L15 10l-5.833 5.834',
        paint: 'stroke',
        strokeWidth: 1.5,
        ink: null,
    },
    /**
     * bg — MEASURED, class channel + asset channel, no dump involved.
     *
     * BOX: `w-4 h-4` = 16x16, byte-identical in nine captures at six widths —
     * bg-home-m-375:18, bg-home-m-440, bg-search-m-375, bg-search-m-440,
     * bg-plp-m-440 (all `left-0`/`right-0`), bg-home-d-1024 (`left-6`),
     * bg-home-d-1280, bg-search-d-1280, bg-pdp-d-1440 (all `left-[68px]`).
     * The box holds across every tier; only the inset steps, and that is
     * --ticker-arrow-inset in tokens/brand-bg.css.
     *
     * GLYPH + INK: the asset channel. The button carries `bg-black/0
     * text-black/0 text-[0px] leading-[0px]`, so nothing textual paints — the
     * chevron IS the button's own `backgroundImage`, an inline base64 SVG that
     * decodes offline (bg-home-m-375:18):
     *   viewBox="0 0 307.2 512"  fill="#CCC"  on both paths, verbatim below.
     * Under live's `bg-contain` a 0.6-aspect artwork in a 16px box paints
     * 9.6 x 16 centred — 3.2..12.8, ink centre 8.0 at inset 0. The owner's own
     * pixels agree: `.reference/live-shots/bg-search-390.png`, sRGB-converted,
     * puts the left chevron's ink at CSS x 2.8..12.2 (9.4 wide, centre 7.5).
     *
     * #CCC IS A REAL BRAND DIVERGENCE, not a capture artefact: NM's ink is pure
     * white on the owner's NM pixels and BG's is a light grey in BG's own
     * artwork. We painted BG's like NM's until this landed.
     *
     * It is rendered as an inline SVG rather than as a background-image because
     * that is how the sibling NM arm is built and because `currentColor` then
     * carries the ink; the painted result is identical to live's `bg-contain`
     * since both size the glyph by its intrinsic 0.6 aspect.
     */
    bg: {
        boxSize: 16,
        viewBox: '0 0 307.2 512',
        left: 'M307.2 31 274.1 0 0 256l274.1 256 33.1-30.9L66.4 256z',
        right: 'm0 481 33.1 31 274.1-256L33.1 0 0 30.9 240.8 256z',
        paint: 'fill',
        ink: '#CCC',
    },
};
